import { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { HiOutlineShoppingBag, HiOutlineMenu, HiOutlineX } from 'react-icons/hi';
import Logo from './Logo';
import SmartLink, { isSameRoute } from './SmartLink';
import { useCart } from '../context/CartContext';
import { useNavTheme } from '../hooks/useNavTheme';
import { flushScrollToTop } from '../utils/scrollLock';

const navLinks = [
  { to: '/', label: 'Home' },
  { to: '/about', label: 'About' },
  { to: '/shop', label: 'Shop' },
  { to: '/return-policy', label: 'Return Policy' },
  { to: '/contact', label: 'Contact' },
];

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { itemCount, toggleDrawer } = useCart();
  const location = useLocation();
  const theme = useNavTheme();
  const closeMobile = () => setMobileOpen(false);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  const handleNavClick = (to) => (event) => {
    if (isSameRoute(location.pathname, to)) {
      event.preventDefault();
    }
    closeMobile();
    flushScrollToTop();
  };

  return (
    <>
      <header
        data-site-header
        style={{ backgroundColor: theme.headerBg }}
        className="fixed top-0 left-0 right-0 z-50 py-3 transition-[background-color] duration-150 ease-out shadow-none"
      >
        <nav className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14 flex items-center justify-between gap-6">
          <Logo size="header" />

          <div className="hidden lg:flex items-center justify-center gap-12 flex-1">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === '/'}
                onClick={handleNavClick(link.to)}
                className={`relative py-1 text-[13px] lg:text-sm tracking-[0.22em] uppercase font-medium whitespace-nowrap transition-colors duration-300 ${theme.link}`}
                data-cursor="interactive"
              >
                {({ isActive }) => (
                  <>
                    {link.label}
                    {isActive && (
                      <span className="absolute -bottom-1 left-0 right-0 h-px bg-lumora-gold/80" />
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </div>

          <div className="flex items-center gap-5">
            <button
              type="button"
              onClick={() => toggleDrawer(true)}
              className={`relative transition-colors magnetic-btn ${theme.icon}`}
              aria-label="Open cart"
              data-cursor="interactive"
            >
              <HiOutlineShoppingBag className="w-5 h-5" strokeWidth={1.5} />
              {itemCount > 0 && (
                <span className="absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 bg-lumora-gold text-lumora-dark text-[10px] rounded-full flex items-center justify-center font-semibold">
                  {itemCount}
                </span>
              )}
            </button>

            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              className={`lg:hidden transition-colors ${theme.menuBtn}`}
              aria-label="Open menu"
            >
              <HiOutlineMenu className="w-6 h-6" />
            </button>
          </div>
        </nav>
      </header>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] lg:hidden"
          >
            <div
              className="absolute inset-0 bg-black/70 backdrop-blur-sm"
              onClick={closeMobile}
              aria-hidden
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              className="absolute right-0 top-0 bottom-0 w-[85%] max-w-sm bg-lumora-dark-surface border-l border-lumora-wood/20 flex flex-col"
            >
              <div className="flex justify-between items-center p-6 border-b border-lumora-wood/15">
                <Logo size="header" />
                <button type="button" onClick={closeMobile} className="text-lumora-cream">
                  <HiOutlineX className="w-6 h-6" />
                </button>
              </div>

              <div className="p-8 flex flex-col gap-8 flex-1">
                {navLinks.map((link) => (
                  <SmartLink
                    key={link.to}
                    to={link.to}
                    onClick={closeMobile}
                    className="text-lg tracking-[0.2em] uppercase text-lumora-cream/80 hover:text-lumora-gold transition-colors"
                  >
                    {link.label}
                  </SmartLink>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
